import { FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { LessonTypeSelector } from "./LessonTypeSelector";
import { Control, UseFormSetValue } from "react-hook-form";
import { memo } from "react";
import { useTranslation } from "react-i18next";

interface LessonType {
  value: string;
  description: string;
  durations: string[];
}

interface Template {
  id: string;
  name: string;
  tags: Array<{
    id: string;
    category: string;
    subcategory?: string;
    tagText: string;
    general: boolean;
  }>;
}

interface LessonTypesSectionProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  lessonTypes: LessonType[];
  showAllTypes: boolean;
  onToggleShowAll: () => void;
  typeError?: string;
  getTemplatesForType: (typeName: string) => Template[];
  isTemplateFeatureEnabled: boolean;
  onTemplateUpload: () => void;
  getBadgeStyles: (category?: string, subcategory?: string) => string;
  onTypeCheck?: (typeIndex: number, checked: boolean) => void;
}

export const LessonTypesSection = memo(function LessonTypesSection({
  control,
  setValue,
  lessonTypes,
  showAllTypes,
  onToggleShowAll,
  typeError,
  getTemplatesForType,
  isTemplateFeatureEnabled,
  onTemplateUpload,
  getBadgeStyles,
  onTypeCheck,
}: LessonTypesSectionProps) {
  const { t } = useTranslation(["lessonLab", "common"]);

  return (
    <fieldset
      className="flex flex-col gap-3 mt-3 2xl:gap-3 2xl:mt-3 @container"
      data-testid="lesson-types"
    >
      <legend className="text-form-title">{t("materials.label")}</legend>
      <FormDescription className="!text-content !text-light-grey mb-1">
        {t("materials.description")}
      </FormDescription>
      {typeError && (
        <p className="p-1 text-sm font-medium rounded text-destructive-foreground bg-destructive w-fit">
          {typeError}
        </p>
      )}

      <div className="flex flex-col gap-3">
        {lessonTypes
          .slice(0, showAllTypes ? lessonTypes.length : 4)
          .map((type, idx) => (
            <LessonTypeSelector
              key={type.value}
              type={type}
              typeIndex={idx}
              control={control}
              setValue={setValue}
              availableTemplates={getTemplatesForType(type.value)}
              isTemplateFeatureEnabled={isTemplateFeatureEnabled}
              onTemplateUpload={onTemplateUpload}
              getBadgeStyles={getBadgeStyles}
              onTypeCheck={onTypeCheck}
            />
          ))}
      </div>

      <Button
        onClick={onToggleShowAll}
        className="self-end mt-4 underline w-fit underline-offset-[5px] text-small-content font-semibold decoration-2"
        variant="ghost"
        type="button"
      >
        {showAllTypes ? t("materials.showLess") : t("materials.showMore")}
      </Button>
    </fieldset>
  );
});
